import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Award, User, Mail, MapPin, Hash, CreditCard, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { NDABanner } from "@/components/NDABanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClaimForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { code, amount } = location.state || {};

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    ssn: "",
    claim_number: ""
  });
  const [loading, setLoading] = useState(false);
  const [ndaAgreed, setNdaAgreed] = useState(false);

  if (!code) {
    navigate("/");
    return null;
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!ndaAgreed) {
      toast.error("Please agree to the Confidentiality & NDA terms to submit your claim");
      return;
    }

    if (!formData.name || !formData.email || !formData.phone || !formData.address || !formData.ssn || !formData.claim_number) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/submit-claim`, {
        code,
        ...formData
      });

      toast.success("Claim submitted successfully!");
      navigate("/success", { state: { amount, name: formData.name } });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit claim");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 py-12 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Prize Banner */}
        <motion.div
          className="bg-gradient-to-r from-primary via-accent to-secondary p-8 rounded-3xl shadow-2xl mb-8 text-center"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Award className="w-16 h-16 mx-auto mb-4 text-white" />
          <h1 className="font-heading text-4xl md:text-6xl text-white mb-2">CONGRATULATIONS!</h1>
          <p className="text-white/90 text-xl mb-4">Your verified prize amount:</p>
          <p className="font-heading text-6xl md:text-7xl text-white">${amount?.toLocaleString()}</p>
        </motion.div>

        {/* NDA Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8"
        >
          <NDABanner agreed={ndaAgreed} onAgreeChange={setNdaAgreed} />
        </motion.div>

        {/* Form Card */}
        <motion.div
          className="bg-white/90 backdrop-blur-xl p-8 md:p-12 rounded-3xl shadow-2xl border-4 border-purple-100"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <h2 className="font-heading text-3xl text-primary mb-6 uppercase text-center">
            Complete Your Claim
          </h2>
          <p className="text-foreground/70 mb-8 text-center">
            Please provide your details to process your prize claim. All information is securely encrypted.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" className="flex items-center gap-2 mb-2 font-bold text-primary">
                <User className="w-5 h-5" />
                Full Legal Name
              </Label>
              <Input
                data-testid="name-input"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="John Doe"
                className="h-14 rounded-xl border-2 focus:border-primary text-lg"
              />
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center gap-2 mb-2 font-bold text-primary">
                <Mail className="w-5 h-5" />
                Email Address
              </Label>
              <Input
                data-testid="email-input"
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="john@example.com"
                className="h-14 rounded-xl border-2 focus:border-primary text-lg"
              />
            </div>

            <div>
              <Label htmlFor="phone" className="flex items-center gap-2 mb-2 font-bold text-primary">
                <Phone className="w-5 h-5" />
                Phone Number
              </Label>
              <Input
                data-testid="phone-input"
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="(555) 123-4567"
                className="h-14 rounded-xl border-2 focus:border-primary text-lg"
              />
            </div>

            <div>
              <Label htmlFor="address" className="flex items-center gap-2 mb-2 font-bold text-primary">
                <MapPin className="w-5 h-5" />
                Full Mailing Address
              </Label>
              <Textarea
                data-testid="address-input"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="123 Main St, City, State, ZIP"
                className="min-h-24 rounded-xl border-2 focus:border-primary text-lg"
              />
            </div>

            <div>
              <Label htmlFor="ssn" className="flex items-center gap-2 mb-2 font-bold text-primary">
                <CreditCard className="w-5 h-5" />
                Social Security Number
              </Label>
              <Input
                data-testid="ssn-input"
                id="ssn"
                name="ssn"
                type="text"
                value={formData.ssn}
                onChange={handleChange}
                placeholder="XXX-XX-XXXX"
                className="h-14 rounded-xl border-2 focus:border-primary text-lg"
                maxLength={11}
              />
            </div>

            <div>
              <Label htmlFor="claim_number" className="flex items-center gap-2 mb-2 font-bold text-primary">
                <Hash className="w-5 h-5" />
                Claim Reference Number
              </Label>
              <Input
                data-testid="claim-number-input"
                id="claim_number"
                name="claim_number"
                value={formData.claim_number}
                onChange={handleChange}
                placeholder="CLM-12345"
                className="h-14 rounded-xl border-2 focus:border-primary text-lg"
              />
            </div>

            <div className="pt-6">
              <Button
                data-testid="submit-claim-button"
                type="submit"
                disabled={loading}
                className="w-full h-16 text-xl font-bold rounded-full bg-gradient-to-r from-primary to-purple-700 hover:from-purple-700 hover:to-primary shadow-[0_6px_0_0_rgba(0,0,0,0.2)] active:shadow-none active:translate-y-[6px] transition-all"
              >
                {loading ? "Submitting..." : "Submit Claim"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
}